import React from 'react';
import { 
  Box,
  Tooltip,
  IconButton,
  Chip,
  LinearProgress,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';

const ResearchJobsTable = ({ 
  jobs, 
  isLoading,
  onViewResults,
  onPauseJob,
  onResumeJob,
  onDeleteJob
}) => {
  // Define how to format dates consistently - using the proven implementation from JobItem
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      // Create a date object from the ISO string
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—'; // Handle invalid dates
      
      // Use the exact same formatting as the original JobItem component
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error('Date parsing error:', e);
      return '—'; // Handle date parsing exceptions
    }
  };

  // Define columns for the DataGrid with useMemo to prevent unnecessary recalculations
  const columns = React.useMemo(() => [

    { 
      field: 'instructions', 
      headerName: 'Job Name', 
      width: 250, // Fixed width instead of flex
      // Truncate long instruction text as was done in original JobItem.js
      renderCell: (params) => {
        if (!params || !params.row) return <Typography>Unnamed Job</Typography>;
        
        // Access job data

        
        // Get job name, with fallbacks to instructions or sourceFilterId
        const jobName = params.row.name || params.row.instructions || params.row.sourceFilterId || '';
        
        // Truncate text if too long (similar to JobItem.js)
        const truncateText = (text, maxWords = 10) => {
          if (!text) return 'Unnamed Job';
          const words = text.split(' ');
          if (words.length <= maxWords) return text;
          return words.slice(0, maxWords).join(' ') + '...';
        };
        
        return <Typography>{truncateText(jobName) || 'Unnamed Job'}</Typography>;
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150, // Fixed width
      renderCell: (params) => {
        // Map status to appropriate colors
        const getStatusColor = (status) => {
          switch(status.toLowerCase()) {
            case 'completed': return 'success';
            case 'processing': return 'primary';
            case 'paused': return 'warning';
            case 'error': case 'failed': return 'error';
            case 'queued': return 'info';
            default: return 'default';
          }
        };

        // Format status text for display
        const formatStatus = (status) => {
          switch(status.toLowerCase()) {
            case 'processing': return 'Processing';
            default: return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
          }
        };

        return (
          <Chip 
            label={formatStatus(params.value)} 
            color={getStatusColor(params.value)} 
            size="small"
            variant="filled"
          />
        );
      }
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 180, // Fixed width
      // Use renderCell instead of valueFormatter - same pattern as job name column
      renderCell: (params) => {
        // Access the date directly from the row object
        const job = params.row;
        if (!job) return <Typography>—</Typography>;
        
        // Get date from createdAt
        const dateString = job.createdAt;
        const formattedDate = formatDate(dateString);
        
        return <Typography>{formattedDate}</Typography>;
      }
    },
    {
      field: 'completion',
      headerName: 'Completion',
      width: 180, // Fixed width
      renderCell: (params) => {
        const job = params.row;
        // Make sure we handle possible inconsistent field names
        const totalWebsites = job.totalWebsites || 0;
        const processedWebsites = job.processedWebsites || 0;
        const progress = totalWebsites > 0 
          ? Math.round((processedWebsites / totalWebsites) * 100) 
          : 0;
        
        return (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="textSecondary">
                Processed: {processedWebsites}/{totalWebsites}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {progress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        );
      }
    },
    {
      field: 'keptTotal',
      headerName: 'Kept/Total',
      width: 120, // Fixed width
      renderCell: (params) => {
        const job = params.row;
        // Use the keptCount directly from the backend
        const keptCount = job.keptCount !== undefined ? job.keptCount : 0;
        // Use processed websites as the total count
        const totalCount = job.processedWebsites || 0;
        
        return (
          <Typography variant="body2">
            {keptCount}/{totalCount}
          </Typography>
        );
      }
    },
    {
      field: 'averageRelevanceScore',
      headerName: 'Avg. Relevance',
      width: 150, // Fixed width
      renderCell: (params) => {
        const job = params.row;
        // Use the avgRelevanceScore directly from the backend
        const score = job.avgRelevanceScore;
        
        if (score === null || score === undefined) return '—';
        
        // Handle both percentage (0-100) and decimal (0-1) formats
        let scoreValue = parseFloat(score);
        let scorePercent;
        
        if (isNaN(scoreValue)) {
          return '—';
        } else if (scoreValue > 0 && scoreValue <= 10) {
          // If score is on a scale of 1-10, convert to percentage
          scorePercent = Math.round(scoreValue * 10);
        } else {
          // If score is already a percentage
          scorePercent = Math.round(scoreValue);
        }
        
        return (
          <Typography variant="body2">
            {scorePercent}%
          </Typography>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 150, // Fixed width
      renderCell: (params) => {
        const job = params.row;
        const isPaused = job.status.toLowerCase() === 'paused';
        const isRunning = job.status.toLowerCase() === 'processing';
        const isFailed = job.status.toLowerCase() === 'failed';
        const isCreated = job.status.toLowerCase() === 'created';
        const isCompleted = job.status.toLowerCase() === 'completed';
        const canResume = isPaused || isRunning || isFailed || isCreated;  // Resume for all except completed
        const canPause = isRunning;
        
        return (
          <Box>
            <Tooltip title="View Results">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onViewResults(job);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {canPause && (
              <Tooltip title="Pause Job">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onPauseJob(job);
                  }}
                >
                  <PauseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            {canResume && (
              <Tooltip title="Resume Job">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onResumeJob(job);
                  }}
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            <Tooltip title="Delete Job">
              <IconButton 
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteJob(job);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ], [onViewResults, onPauseJob, onResumeJob, onDeleteJob]); // Include handler functions as dependencies

  // Normalize row data for DataGrid to ensure all fields are at the top level
  const getRowsWithId = React.useMemo(() => {
    return jobs.map(job => ({
      // ID field required by DataGrid
      id: job.jobId,
      // Copy all job fields
      ...job,
      // Add the createdAt explicitly at the top level for the column
      createdAt: job.createdAt || job.created_at || null
    }));
  }, [jobs]);

  
  // Calculate total width of all columns to ensure proper sizing
  const totalColumnsWidth = React.useMemo(() => {
    return columns.reduce((sum, column) => sum + (column.width || 0), 0);
  }, [columns]);

  return (
    <Box sx={{ 
      height: 500, 
      width: '100%', 
      overflow: 'hidden', 
      boxSizing: 'border-box',
      display: 'flex',
      justifyContent: 'center',
      '& > div': { maxWidth: '100%' }
    }}>
      <DataGrid
        rows={getRowsWithId}
        columns={columns}
        getRowId={(row) => row.id || row.jobId}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        disableColumnMenu
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
          sorting: {
            sortModel: [{ field: 'createdAt', sort: 'desc' }],
          },
        }}
        disableRowSelectionOnClick
        sx={{
          width: `${totalColumnsWidth}px`,
          maxWidth: '100%',
          '& .MuiDataGrid-main': {
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          },
          '& .MuiDataGrid-columnHeadersInner': {
            width: '100% !important',
            maxWidth: '100% !important'
          },
          '& .MuiDataGrid-virtualScroller': {
            width: '100% !important',
            maxWidth: '100% !important',
            overflowX: 'hidden !important'
          },
          '& .MuiDataGrid-virtualScrollerRenderZone': {
            width: '100% !important',
            maxWidth: '100% !important'
          },
          border: 'none',
          backgroundColor: '#1e2738', // Same as inner box color
          '& .MuiDataGrid-cell': {
            py: 1,
            border: 'none'
          },
          '& .MuiDataGrid-columnHeaders': {
            border: 'none'
          },
          '& .MuiDataGrid-footerContainer': {
            border: 'none'
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none'
          },
          '& .MuiDataGrid-row': {
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          },
          // Reduce font size for Job Name and Created columns
          '& .MuiDataGrid-cell[data-field="jobName"], & .MuiDataGrid-cell[data-field="createdAt"]': {
            fontSize: '0.8rem'
          },
          '& .MuiDataGrid-columnHeader[data-field="jobName"], & .MuiDataGrid-columnHeader[data-field="createdAt"]': {
            fontSize: '0.8rem'
          }
        }}
      />
    </Box>
  );

};

export default ResearchJobsTable;
